/**
 * ФП рядом с первоначальным расчётом себестоимости (БМ-18): плановые закупки и расходы против плановой
 * себестоимости бюджета, финансовый результат против плановой маржи; ниже — по статьям ФП.
 */
import type { FinPlanBaseline } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { percent, tengeShort } from '../../lib/format';
import { Panel } from '../../ui/layout';

function articleLabel(article: string | null): string {
  return article ?? 'Без статьи / не разнесено';
}

export function FinPlanBaselinePanel({
  projectId,
  planId,
  stamp,
}: {
  projectId: string;
  planId: string;
  /** Меняется при сохранении плана: расчёт читается заново */
  stamp: string;
}) {
  const q = useQuery({
    queryKey: ['finplan', 'baseline', projectId, planId, stamp],
    queryFn: () =>
      api<FinPlanBaseline | null>(`/projects/${projectId}/fin-plans/baseline`, {
        query: { versionId: planId },
      }),
    retry: false,
  });
  // Нет права на бюджет или бюджета у проекта нет — блок не показываем
  if (!q.data?.budgetVersionId) return null;
  const b = q.data;
  const rows: Array<{ label: string; budget: string; plan: string; diff?: number }> = [
    {
      label: 'Себестоимость и расходы',
      budget: tengeShort(b.budget.plannedCost),
      plan: tengeShort(b.plan.purchase + b.plan.payroll + b.plan.admin),
      diff: b.diff.cost,
    },
    {
      label: 'Финансовый результат',
      budget: tengeShort(b.budget.plannedMargin),
      plan: tengeShort(b.plan.result),
      diff: b.diff.result,
    },
    {
      label: 'Маржа',
      budget: percent(b.budget.plannedMarginPct),
      plan: percent(b.plan.marginPct),
    },
  ];
  return (
    <Panel
      title="План рядом с расчётом себестоимости"
      meta={`БМ-18: ФП версии ${b.planNumber} против бюджета проекта`}
      bodyClassName="p-0"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted">
              <th className="px-4 py-2 font-medium" />
              <th className="px-3 py-2 text-right font-medium">Расчёт (бюджет)</th>
              <th className="px-3 py-2 text-right font-medium">Финансовый план</th>
              <th className="px-4 py-2 text-right font-medium">Разница</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-border last:border-0">
                <td className="px-4 py-2">{r.label}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.budget}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.plan}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {r.diff === undefined ? '' : tengeShort(r.diff)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {b.byArticle.length > 0 ? (
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted">
                <th className="px-4 py-2 font-medium">Статья расходов</th>
                <th className="px-3 py-2 text-right font-medium">Расчёт (бюджет)</th>
                <th className="px-3 py-2 text-right font-medium">Финансовый план</th>
                <th className="px-4 py-2 text-right font-medium">Разница</th>
              </tr>
            </thead>
            <tbody>
              {b.byArticle.map((r) => (
                <tr key={r.article ?? '—'} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">{articleLabel(r.article)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{tengeShort(r.budget)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{tengeShort(r.plan)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{tengeShort(r.diff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <p className="px-4 py-2 text-[13px] text-ink-2">
        В расходах плана: закупки {tengeShort(b.plan.purchase)}, персонал {tengeShort(b.plan.payroll)},
        административные {tengeShort(b.plan.admin)}; налоги {tengeShort(b.plan.tax)} и плата за деньги{' '}
        {tengeShort(b.plan.costOfMoney)} сравниваются только в финансовом результате. По статьям — без
        налогов; разделы бюджета без выбранного варианта — в «Без статьи / не разнесено».
      </p>
    </Panel>
  );
}
